import { useState, useEffect, useCallback, useMemo } from "react";
import { aiSpendingApi } from "@services/api/organizations";
import { LineChart } from "@components/charts/LineChart";
import type { LineChartDataPoint } from "@components/charts/LineChart";

type DayStat = { date: string; call_count: number; total_cost: number };

function fmtEur(v: number, decimals = 2): string {
  return v.toLocaleString("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + "\u00A0\u20AC";
}

const CALL_TYPE_LABELS: Record<string, string> = {
  document_classification: "Analyse de document",
  carte_grise_validation: "Validation carte grise",
};

export function AiSettings({ hideSoldeRecharge = false }: { hideSoldeRecharge?: boolean } = {}) {
  const [spending, setSpending] = useState<{
    solde: number;
    total_spent: number;
    transaction_count: number;
  }>({ solde: 0, total_spent: 0, transaction_count: 0 });
  const [dailyStats, setDailyStats] = useState<DayStat[]>([]);
  const [transactions, setTransactions] = useState<
    {
      id: string;
      call_type: string;
      reference_id: string;
      model: string;
      unit_cost: number;
      created_at: string;
    }[]
  >([]);
  const [txPage, setTxPage] = useState(0);
  const TX_PER_PAGE = 20;

  const fetchSpending = useCallback(async () => {
    try {
      const [s, daily, tx] = await Promise.all([
        aiSpendingApi.getSpending(),
        aiSpendingApi.getDailyStats(30),
        aiSpendingApi.getTransactions(TX_PER_PAGE, txPage * TX_PER_PAGE),
      ]);
      setSpending(s);
      setDailyStats(daily);
      setTransactions(tx);
    } catch {
      // API not available yet
    }
  }, [txPage]);

  useEffect(() => {
    fetchSpending();
  }, [fetchSpending]);

  const callsChartData: LineChartDataPoint[] = useMemo(
    () =>
      dailyStats.map((d) => {
        const date = new Date(d.date);
        return {
          label: date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", ""),
          tooltipLabel: date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }),
          value: d.call_count,
        };
      }),
    [dailyStats]
  );

  return (
    <div className="flex flex-col gap-6">
      <h2
        className="text-h1 font-semibold"
        style={{ fontFamily: "Poppins", color: "#111827" }}
      >
        IA - Consommation
      </h2>

      {!hideSoldeRecharge && (
        <div className="flex flex-row gap-4">
          <div
            className="flex flex-col flex-1 p-5 rounded-xl"
            style={{
              background: spending.solde > 0 ? "#F0FDF4" : "#FEF2F2",
              border: `1px solid ${spending.solde > 0 ? "#BBF7D0" : "#FECACA"}`,
            }}
          >
            <span
              className="text-h3 font-medium mb-1"
              style={{ fontFamily: "Poppins", color: "#6B7280" }}
            >
              Solde actuel
            </span>
            <span
              className="text-hx font-bold"
              style={{
                fontFamily: "Poppins",
                color: spending.solde > 0 ? "#166534" : "#991B1B",
              }}
            >
              {fmtEur(spending.solde)}
            </span>
          </div>
          <div
            className="flex flex-col flex-1 p-5 rounded-xl"
            style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
          >
            <span
              className="text-h3 font-medium mb-1"
              style={{ fontFamily: "Poppins", color: "#6B7280" }}
            >
              Total depense (IA)
            </span>
            <span
              className="text-hx font-bold"
              style={{ fontFamily: "Poppins", color: "#111827" }}
            >
              {fmtEur(spending.total_spent)}
            </span>
          </div>
          <div
            className="flex flex-col flex-1 p-5 rounded-xl"
            style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
          >
            <span
              className="text-h3 font-medium mb-1"
              style={{ fontFamily: "Poppins", color: "#6B7280" }}
            >
              Nombre d'appels
            </span>
            <span
              className="text-hx font-bold"
              style={{ fontFamily: "Poppins", color: "#111827" }}
            >
              {spending.transaction_count}
            </span>
          </div>
        </div>
      )}

      {/* Stat cards when inside global tab */}
      {hideSoldeRecharge && (
        <div className="flex flex-row gap-4">
          <div
            className="flex flex-col flex-1 p-5 rounded-xl"
            style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
          >
            <span
              className="text-h3 font-medium mb-1"
              style={{ fontFamily: "Poppins", color: "#6B7280" }}
            >
              Total depense (IA)
            </span>
            <span
              className="text-hx font-bold"
              style={{ fontFamily: "Poppins", color: "#111827" }}
            >
              {fmtEur(spending.total_spent)}
            </span>
          </div>
          <div
            className="flex flex-col flex-1 p-5 rounded-xl"
            style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
          >
            <span
              className="text-h3 font-medium mb-1"
              style={{ fontFamily: "Poppins", color: "#6B7280" }}
            >
              Nombre d'appels
            </span>
            <span
              className="text-hx font-bold"
              style={{ fontFamily: "Poppins", color: "#111827" }}
            >
              {spending.transaction_count}
            </span>
          </div>
        </div>
      )}

      {/* Chart */}
      <div
        className="p-5 rounded-xl"
        style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
      >
        <LineChart
          data={callsChartData}
          title="Appels IA (30 jours)"
          valueSuffix=" appel(s)"
          color="#8B5CF6"
          emptyMessage="Aucun appel IA sur cette periode"
          formatValue={(v) => `${v}`}
        />
      </div>

      {/* Pricing table */}
      <div
        className="flex flex-col gap-2 px-4 py-3 rounded-lg"
        style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
      >
        <span
          className="text-h3 font-medium mb-1"
          style={{ fontFamily: "Poppins", color: "#374151" }}
        >
          Tarification
        </span>
        <div className="flex flex-row justify-between">
          <span className="text-h4" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
            Analyse de document
          </span>
          <span className="text-h4 font-medium" style={{ fontFamily: "Poppins", color: "#374151" }}>
            0,01{"\u00A0"}{"\u20AC"}
          </span>
        </div>
        <div className="flex flex-row justify-between">
          <span className="text-h4" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
            Validation carte grise
          </span>
          <span className="text-h4 font-medium" style={{ fontFamily: "Poppins", color: "#374151" }}>
            0,01{"\u00A0"}{"\u20AC"}
          </span>
        </div>
      </div>

      {/* Transaction history */}
      {transactions.length > 0 && (
        <div className="flex flex-col gap-3">
          <span
            className="text-h2 font-semibold"
            style={{ fontFamily: "Poppins", color: "#374151" }}
          >
            Historique des transactions
          </span>
          <table
            className="w-full text-h3"
            style={{ fontFamily: "Poppins", borderCollapse: "collapse" }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid #E5E7EB" }}>
                <th className="text-left py-2 font-medium" style={{ color: "#6B7280" }}>
                  Date
                </th>
                <th className="text-left py-2 font-medium" style={{ color: "#6B7280" }}>
                  Type
                </th>
                <th className="text-left py-2 font-medium" style={{ color: "#6B7280" }}>
                  Modele
                </th>
                <th className="text-right py-2 font-medium" style={{ color: "#6B7280" }}>
                  Cout
                </th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr
                  key={tx.id}
                  style={{ borderBottom: "1px solid #F3F4F6" }}
                >
                  <td className="py-2" style={{ color: "#374151" }}>
                    {new Date(tx.created_at).toLocaleString("fr-FR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="py-2" style={{ color: "#374151" }}>
                    {CALL_TYPE_LABELS[tx.call_type] || tx.call_type}
                  </td>
                  <td className="py-2" style={{ color: "#6B7280" }}>
                    {tx.model}
                  </td>
                  <td className="py-2 text-right" style={{ color: "#374151" }}>
                    {fmtEur(tx.unit_cost, 4)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex flex-row items-center gap-3 mt-1">
            <button
              onClick={() => setTxPage((p) => Math.max(0, p - 1))}
              disabled={txPage === 0}
              className="text-h3 font-medium px-3 py-1 rounded"
              style={{
                fontFamily: "Poppins",
                color: txPage === 0 ? "#D1D5DB" : "#374151",
                border: "1px solid #E5E7EB",
                cursor: txPage === 0 ? "default" : "pointer",
              }}
            >
              Precedent
            </button>
            <span
              className="text-h4"
              style={{ fontFamily: "Poppins", color: "#6B7280" }}
            >
              Page {txPage + 1}
            </span>
            <button
              onClick={() => setTxPage((p) => p + 1)}
              disabled={transactions.length < TX_PER_PAGE}
              className="text-h3 font-medium px-3 py-1 rounded"
              style={{
                fontFamily: "Poppins",
                color: transactions.length < TX_PER_PAGE ? "#D1D5DB" : "#374151",
                border: "1px solid #E5E7EB",
                cursor: transactions.length < TX_PER_PAGE ? "default" : "pointer",
              }}
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
